import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import Grid from '@material-ui/core/Grid'

import {
  TravelDataType,
  TravelContextType,
  AttachmentType,
  PartType,
  CaseTypeType,
} from '../../models'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import { useStyles } from '../../pages/styled'
import theme from '../../theme'
import { dateToISO, isoDateStrToDateOrNull } from '../../helpers'
import TextField from '../TextField'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import { DatePicker } from '../DatePicker'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'

export const TripForm = (params: {
  caseReportId: string
  tripFormData: TravelContextType | undefined
  onPageContinue: (tripData: TravelDataType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  errorMessage: string
  onDataChanged: (isChanged: boolean) => void
  caseType: CaseTypeType | null
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const handleFormSubmit = (values: any) => {
    const tripDataParams: TravelDataType =
      transformFormikValueToTripData(values)

    params.onPageContinue(tripDataParams)
  }

  const transformFormikValueToTripData = (values: any) => {
    return {
      travelType: values?.travelType,
      touroperator:
        values?.travelType === 'T' ? values?.touroperator : undefined,
      companyName: values?.travelType === 'B' ? values?.companyName : undefined,
      departureDate: dateToISO(values?.insuredFrom),
      returnDate: dateToISO(values.insuredTo),
      attachmentList: combineAttachmentsList(
        params.tripFormData?.attachmentList, // original
        [
          {
            attachmentType: AttachmentType.POLICY,
            attachments: values.attachments,
          },
        ]
      ),
    }
  }

  const transformInputToFormikValues = (
    values: TravelContextType | undefined
  ) => {
    return {
      travelType: values?.travelType ?? null,
      touroperator: values?.touroperator ?? '',
      companyName: values?.companyName ?? '',
      insuredFrom: isoDateStrToDateOrNull(values?.departureDate),
      insuredTo: isoDateStrToDateOrNull(values?.returnDate),
      attachments: getAttachmentsFromAttachmentListOrEmpty(
        values?.attachmentList,
        AttachmentType.POLICY
      ),
    }
  }

  const TripSchema = () =>
    yup.object().shape({
      travelType: yup.mixed().nullable().required(t('optionRequired')),
      touroperator: yup.string().when('travelType', {
        is: (travelType: string) => travelType === 'T',
        then: yup
          .string()
          .required(t('tripForm.touroperator.required'))
          .min(2, t('lengthText')),
      }),
      companyName: yup.string().when('travelType', {
        is: (travelType: string) => travelType === 'B',
        then: yup
          .string()
          .required(t('tripForm.companyName.required'))
          .min(2, t('lengthText')),
      }),
      insuredFrom: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      insuredTo: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .min(yup.ref('insuredFrom'), t('tripForm.insuredTo.err'))
        .typeError(t('dateMessage.err1')),
      attachments: yup.array().nullable().min(1, t('uploadFile.err1')),
    })

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'TripForm'} >
      <Formik
        initialValues={transformInputToFormikValues(params.tripFormData)}
        validationSchema={TripSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleChange,
          touched,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form>
              <Grid
                item
                xs={12}
                sm={12}
                xl={12}
                container
                className={classes.gridItem}
                spacing={2}
              >
                <Attachments
                  name={'attachments'}
                  caseReportId={params.caseReportId}
                  labelText={t('uploadAttachments.CASE.POLICY')}
                  attachmentType={AttachmentType.POLICY}
                  part={PartType.CASE}
                  attachments={values.attachments}
                  onAttachmentsChanged={(event: AttachmentsChangedEvent) => {
                    // aktualizace values formiku
                    setFieldValue('attachments', event.value.attachments)
                  }}
                  onBlur={handleBlur}
                  required={true}
                  error={
                    Boolean(errors.attachments && touched.attachments)
                  }
                  helperText={
                    errors.attachments && touched.attachments
                      ? errors.attachments
                      : null
                  }
                />
                <Grid
                  item
                  xs={12}
                  sm={12}
                  xl={12}
                  container
                  className={classes.gridItem}
                  spacing={2}
                >
                  <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                    <RadioButtonsField
                      aria-label="travelType"
                      name="travelType"
                      label={t('tripForm.travelType.label')}
                      required
                      value={values.travelType}
                      onChange={handleChange}
                      lookup={objectToLookup(t('tripForm.travelType.code', { returnObjects: true }))}
                      error={Boolean(errors.travelType && touched.travelType)}
                      helperText={errors.travelType && touched.travelType
                        ? errors.travelType
                        : null}
                    />
                  </Grid>
                  {values.travelType === 'T' && (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <TextField
                          label={t('tripForm.touroperator.label')}
                          placeholder={t('tripForm.touroperator.hint')}
                          error={!!(errors.touroperator && touched.touroperator)}
                          name="touroperator"
                          value={values.touroperator}
                          onChange={handleChange}
                          helperText={
                            errors.touroperator && touched.touroperator
                              ? errors.touroperator
                              : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{
                            maxLength: 50,
                          }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                  )}
                  {values.travelType === 'B' && (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <TextField
                          name="companyName"
                          label={t('tripForm.companyName.label')}
                          placeholder={t('tripForm.companyName.hint')}
                          error={!!(errors.companyName && touched.companyName)}
                          value={values.companyName}
                          onChange={handleChange}
                          helperText={
                            errors.companyName && touched.companyName
                              ? errors.companyName
                              : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{
                            maxLength: 50,
                          }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  xl={12}
                  container
                  className={classes.gridItem}
                  spacing={2}
                >
                  <Grid item sm={6} xs={6} xl={6}>
                    <DatePicker
                      name="insuredFrom"
                      label={
                        params.caseType === 'CA'
                          ? t('tripForm.insuredFrom.labelCA')
                          : t('tripForm.insuredFrom.label')
                      }
                      error={Boolean(errors.insuredFrom && touched.insuredFrom)}
                      helperText={
                        errors.insuredFrom && touched.insuredFrom
                          ? errors.insuredFrom
                          : null
                      }
                      required={true}
                      minDate={new Date(2000, 0, 1)}
                      maxDate={
                        params.caseType === 'CA'
                          ? new Date(2030, 12, 31)
                          : new Date()
                      }
                      value={values.insuredFrom}
                      onChange={(value) => {
                        setFieldValue('insuredFrom', value)
                      }}
                    />
                  </Grid>
                  <Grid item sm={6} xs={6} xl={6}>
                    <DatePicker
                      name="insuredTo"
                      label={t('tripForm.insuredTo.label')}
                      error={Boolean(errors.insuredTo && touched.insuredTo)}
                      helperText={
                        errors.insuredTo && touched.insuredTo
                          ? errors.insuredTo
                          : null
                      }
                      required={true}
                      minDate={
                        values.insuredFrom
                          ? new Date(values.insuredFrom)
                          : new Date(2000, 0, 1)
                      }
                      maxDate={
                        params.caseType === 'CA'
                          ? new Date(2030, 12, 31)
                          : new Date()
                      }
                      value={values.insuredTo}
                      onChange={(value) => {
                        setFieldValue('insuredTo', value)
                      }}
                    />
                  </Grid>
                </Grid>
                <ErrorSubmitBack
                  formik={formikRef.current}
                  pending={params.isContinueInProgress}
                  backLinkText={t('firstPg.backBtn')}
                  onBackLinkClick={params.onPageBack}
                >
                  {t('continueBtn')}
                </ErrorSubmitBack>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}
